import { useForm } from 'react-hook-form';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import {
  AutonomyDeterminationStatus,
  MinorAutonomyEvidenceTypes,
  useCreateSubPatientMutation,
  useGetPatientStateAgeOfMajorityQuery,
} from 'graphql/generated/hasura';
import { useGetPage } from 'hooks/useGetPage';
import { ALLOWED_NAME_CHARACTERS, pageIds } from 'utilities/constants';
import { useAuth } from 'auth/context/AuthContext';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCustomToast } from 'layout/useToast';
import { MY_ACCOUNT_PROFILE } from 'utilities/routes';
import moment from 'moment/moment';
import { formatErrorString } from 'utilities/sanitizer';
import StorageService from 'utilities/storageService';
import { MAIN_ACCOUNT_INFO_KEY } from '../../../../firebase/constants';

const today = new Date();
const MAX_NAME_LENGTH = 50;
const MIN_NAME_LENGTH = 2;
export const MIN_AGE = 13;

const mainAccountInfoStorage = new StorageService(MAIN_ACCOUNT_INFO_KEY);

export enum ChildGenderEnum {
  MALE = 'Male',
  FEMALE = 'Female',
}

export const useCreateSubPatient = () => {
  const { data: locale } = useGetPage({
    pageId: pageIds.ADD_CHILD_PATIENT,
    locale: 'en',
  });
  const mainAccountInfo = mainAccountInfoStorage.getData();
  const name = mainAccountInfo?.name;

  const { handleSelectAccount, refetchSubPatients } = useAuth();
  const { addToast } = useCustomToast();
  const navigate = useNavigate();

  const { data } = useGetPatientStateAgeOfMajorityQuery();

  const noLeadingTrailingSpaceError =
    locale?.errors?.noLeadingTrailingSpacesError ??
    'Name can not begin or end with a space';

  const minBirthDate = useMemo(
    () =>
      new Date(
        today.getFullYear() -
          (data?.getPatientStateAgeOfMajority?.ageOfMajority ?? MIN_AGE),
        today.getMonth(),
        today.getDate(),
      ),
    [data],
  );

  const schema = yup.object({
    lastName: yup
      .string()
      .required(locale?.errors?.fieldRequired)
      .min(
        MIN_NAME_LENGTH,
        formatErrorString(locale?.errors?.minCharInNameError ?? '', {
          fieldName: 'Last name ',
        }),
      )
      .max(
        MAX_NAME_LENGTH,
        formatErrorString(locale?.errors?.maxCharInNameError ?? '', {
          fieldName: 'Last name ',
        }),
      )
      .trim()
      .matches(
        ALLOWED_NAME_CHARACTERS,
        locale?.errors?.noSpecialCharactersInNameError,
      )
      .test(
        'no-numbers',
        formatErrorString(locale?.errors?.noNumberInNameError ?? '', {
          fieldName: 'Last name ',
        }),
        (value) => value === undefined || !/\d/.test(value),
      )
      .test(
        'no-leading-trailing-space',
        noLeadingTrailingSpaceError,
        (value) => value === undefined || value.trim() === value,
      ),

    firstName: yup
      .string()
      .required(locale?.errors?.fieldRequired)
      .min(
        MIN_NAME_LENGTH,
        formatErrorString(locale?.errors?.minCharInNameError ?? '', {
          fieldName: 'First name ',
        }),
      )
      .max(
        MAX_NAME_LENGTH,
        formatErrorString(locale?.errors?.maxCharInNameError ?? '', {
          fieldName: 'First name ',
        }),
      )
      .trim()
      .matches(
        ALLOWED_NAME_CHARACTERS,
        locale?.errors?.noSpecialCharactersInNameError,
      )
      .test(
        'no-numbers',
        formatErrorString(locale?.errors?.noNumberInNameError ?? '', {
          fieldName: 'First name ',
        }),
        (value) => value === undefined || !/\d/.test(value),
      )
      .test(
        'no-leading-trailing-space',
        noLeadingTrailingSpaceError,
        (value) => value === undefined || value.trim() === value,
      ),

    dob: yup
      .date()
      .required(locale?.errors?.fieldRequired)
      .typeError(locale?.errors?.fieldRequired)
      .test(
        'is-major',
        `Your child’s age is above the state’s ${data?.getPatientStateAgeOfMajority?.state} age of majority ${data?.getPatientStateAgeOfMajority?.ageOfMajority}. Only minors can be registered under a parent or legal guardian account`,
        function (value) {
          if (!value) return true;
          return moment(value).isAfter(minBirthDate);
        },
      )
      .test(
        'is-minor',
        locale?.errors?.childTooYoungError ??
          'Date of birth cannot be in the future.',
        function (value) {
          if (!value) return true;
          return moment(value).isSameOrBefore(moment().startOf('day'));
        },
      ),
    gender: yup.string().required(locale?.errors?.fieldRequired),
    isVerifyTheParent: yup.boolean().oneOf([true], 'Check is required'),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      dob: undefined,
      gender: ChildGenderEnum.MALE,
      isVerifyTheParent: false,
    },
  });

  const [createSubPatientMutation, { loading: submitLoading }] =
    useCreateSubPatientMutation({
      awaitRefetchQueries: true,
      refetchQueries: ['GetSubPatientsByParent'],
    });

  const onSubmit = (data: yup.InferType<typeof schema>) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isVerifyTheParent, ...form } = data;

    return createSubPatientMutation({
      variables: {
        ...form,
        dob: moment(data.dob).format('MM/DD/YYYY'),
      },
      onCompleted: (data) => {
        if (handleSelectAccount) {
          handleSelectAccount(data.createSubPatient.id);
          navigate(MY_ACCOUNT_PROFILE);
        }
        refetchSubPatients();
      },
      onError: (error) => addToast({ type: 'warning', message: error.message }),
    });
  };

  const genderOptions = useMemo(
    () => [
      {
        value: ChildGenderEnum.MALE,
        label: locale?.childInformationSection?.male,
      },
      {
        value: ChildGenderEnum.FEMALE,
        label: locale?.childInformationSection?.female,
      },
    ],
    [locale],
  );

  const determinationOptions = useMemo(
    () => [
      {
        value: AutonomyDeterminationStatus.EmancipatedMinor,
        label: locale?.statusDeterminationSection?.emancipated,
        tooltip:
          'An emancipated minor is a person under the age of legal majority who has been legally freed from the control of their parents or guardians. This process, granted by a court or through certain actions, allows the minor to legally make their own decisions about things like living arrangements, employment, and finances, while their parents are no longer responsible for their acts or support',
      },
      {
        value: AutonomyDeterminationStatus.MarriedMinor,
        label: locale?.statusDeterminationSection?.married,
      },
      {
        value: AutonomyDeterminationStatus.MinorParent,
        label: locale?.statusDeterminationSection?.parent,
      },
      {
        value: AutonomyDeterminationStatus.SelfSupporting,
        label: locale?.statusDeterminationSection?.livingApart,
      },
      {
        value: AutonomyDeterminationStatus.HomelessUnaccompanied,
        label: locale?.statusDeterminationSection?.homeless,
      },
      {
        value: AutonomyDeterminationStatus.OtherStateServiceException,
        label: locale?.statusDeterminationSection?.other,
      },
    ],
    [locale],
  );

  const emancipatedOptions = useMemo(
    () => [
      {
        value: MinorAutonomyEvidenceTypes.EmancipatedCertifiedCourtOrder,
        label:
          locale?.evidenceReviewAndVerification?.emancipatedCertifiedCourtOrder,
      },
      {
        value: MinorAutonomyEvidenceTypes.EmancipatedStateId,
        label: locale?.evidenceReviewAndVerification?.emancipatedStateId,
      },
    ],
    [locale],
  );

  const marriedOptions = useMemo(
    () => [
      {
        value: MinorAutonomyEvidenceTypes.MarriedMarriageCertificate,
        label:
          locale?.evidenceReviewAndVerification?.marriedMarriageCertificate,
      },
      {
        value: MinorAutonomyEvidenceTypes.MarriedDivorceDecree,
        label: locale?.evidenceReviewAndVerification?.marriedDivorceDecree,
      },
    ],
    [locale],
  );

  const minorOptions = useMemo(
    () => [
      {
        value: MinorAutonomyEvidenceTypes.MinorParentBirthCertificate,
        label:
          locale?.evidenceReviewAndVerification?.minorParentBirthCertificate,
      },
      {
        value: MinorAutonomyEvidenceTypes.MinorParentMedicalRecord,
        label: locale?.evidenceReviewAndVerification?.minorParentMedicalRecord,
      },
      {
        value: MinorAutonomyEvidenceTypes.MinorParentSelfAttestation,
        label:
          locale?.evidenceReviewAndVerification?.minorParentSelfAttestation,
      },
    ],
    [locale],
  );

  const selfSupportOptions = useMemo(
    () => [
      {
        value: MinorAutonomyEvidenceTypes.SelfSupportingLease,
        label: locale?.evidenceReviewAndVerification?.selfSupportingLease,
      },
      {
        value: MinorAutonomyEvidenceTypes.SelfSupportingIncomeDocument,
        label:
          locale?.evidenceReviewAndVerification?.selfSupportingIncomeDocument,
      },
      {
        value: MinorAutonomyEvidenceTypes.SelfSupportingEmployerLetter,
        label:
          locale?.evidenceReviewAndVerification?.selfSupportingEmployerLetter,
      },
    ],
    [locale],
  );

  const homelessOptions = useMemo(
    () => [
      {
        value: MinorAutonomyEvidenceTypes.HomelessSchoolLiaisonLetter,
        label:
          locale?.evidenceReviewAndVerification?.homelessSchoolLiaisonLetter,
      },
      {
        value: MinorAutonomyEvidenceTypes.HomelessShelterVerification,
        label:
          locale?.evidenceReviewAndVerification?.homelessShelterVerification,
      },
      {
        value: MinorAutonomyEvidenceTypes.HomelessSelfAttestation,
        label: locale?.evidenceReviewAndVerification?.homelessSelfAttestation,
      },
    ],
    [locale],
  );

  const otherOptions = useMemo(
    () => [
      {
        value: MinorAutonomyEvidenceTypes.OtherExceptionVerbal,
        label: locale?.evidenceReviewAndVerification?.otherExceptionVerbal,
      },
      {
        value: MinorAutonomyEvidenceTypes.OtherExceptionOtherDocument,
        label:
          locale?.evidenceReviewAndVerification?.otherExceptionOtherDocument,
      },
    ],
    [locale],
  );

  return {
    methods,
    submitLoading,
    onSubmit,
    genderOptions,
    otherOptions,
    homelessOptions,
    selfSupportOptions,
    minorOptions,
    marriedOptions,
    emancipatedOptions,
    determinationOptions,
    parent: name,
  };
};
